import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, Printer, Download, ChevronDown, ChevronUp, RefreshCw, X, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Hint = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: 'rgba(235, 110, 31, 0.04)', border: '1px solid rgba(235, 110, 31, 0.12)', padding: '0.65rem 0.85rem', marginBottom: '1rem', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
    <HelpCircle size={15} style={{ color: 'var(--astros-orange)', flexShrink: 0, marginTop: 2 }} />
    <span>{text}</span>
  </div>
);

const SubmissionsView = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { limit: 100, page: 1 };
      if (search) params.search = search;
      const res = await axios.get(`${API}/submissions`, { params, headers: { Authorization: `Bearer ${token}` } });
      let subs = res.data.submissions || [];
      if (filter !== 'all') {
        subs = subs.filter(s => {
          if (filter === 'new') return !s.viewed && !s.printed;
          if (filter === 'viewed') return s.viewed && !s.printed;
          if (filter === 'printed') return s.printed;
          return true;
        });
      }
      setSubmissions(subs);
    } catch (err) { console.error(err); toast.error('Failed to load submissions'); }
    finally { setLoading(false); }
  }, [search, filter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const getStatus = (sub) => {
    if (sub.printed) return 'printed';
    if (sub.viewed) return 'viewed';
    return 'new';
  };

  const statusBadge = (sub) => {
    const s = getStatus(sub);
    if (s === 'new') return <span className="badge-new">NEW</span>;
    if (s === 'viewed') return <span className="badge-viewed">VIEWED</span>;
    if (s === 'printed') return <span className="badge-printed">PRINTED</span>;
    return null;
  };

  const handleExpand = async (sub) => {
    if (expandedId === sub.id) {
      setExpandedId(null);
      setExpandedData(null);
      return;
    }
    setExpandedId(sub.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/submissions/${sub.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setExpandedData(res.data);
      // The backend marks viewed on GET, so update local state
      setSubmissions(prev => prev.map(s => s.id === sub.id ? { ...s, viewed: true } : s));
    } catch (err) { console.error(err); toast.error('Failed to load details'); }
  };

  const handlePrint = async (sub, pdfType = 'surety_bond') => {
    setPdfLoading(sub.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/submissions/${sub.id}/pdf/${pdfType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setSubmissions(prev => prev.map(s => s.id === sub.id ? { ...s, printed: true } : s));
      toast.success('PDF opened - use browser print to print');
    } catch (err) {
      console.error(err);
      toast.error('Failed to load PDF');
    } finally { setPdfLoading(null); }
  };

  const handleDownloadAll = async (sub) => {
    const pdfTypes = ['personal_reference', 'surety_bond', 'asset_inventory'];
    const token = localStorage.getItem('token');
    for (const pdfType of pdfTypes) {
      try {
        const res = await axios.get(`${API}/submissions/${sub.id}/pdf/${pdfType}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `LSB_${sub.id}_${pdfType}.pdf`;
        a.click();
      } catch (err) { /* PDF type might not exist */ }
    }
    toast.success('PDFs downloaded');
  };

  const newCount = submissions.filter(s => !s.viewed && !s.printed).length;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="dash-content__title" data-testid="submissions-title">ALL SUBMISSIONS</h1>
        <p className="dash-content__subtitle">
          {submissions.length} total {newCount > 0 && <span style={{ color: 'var(--texas-red)', fontWeight: 700 }}>({newCount} NEW)</span>}
        </p>
      </div>

      <Hint text="This is where every form your clients submit lives. Use the search bar to find anyone by name, SSN last 4, or charge. Click on any row to expand it and see the full details. Use the PRINT button to open the PDFs." />

      {/* Search & Filter */}
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        Use the buttons below to filter: <strong>ALL</strong> = everything, <strong>NEW</strong> = not yet opened, <strong>VIEWED</strong> = opened but not printed, <strong>PRINTED</strong> = already printed.
      </div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }} data-testid="search-filter-bar">
        <div style={{ flex: 1, minWidth: 280, position: 'relative' }}>
          <Search size={20} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" className="form-input" placeholder="Search by name, SSN last 4, charge..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: '2.75rem', fontSize: '1.05rem' }} data-testid="search-input" />
          {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={16} /></button>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['all', 'new', 'viewed', 'printed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={filter === f ? 'btn-primary' : 'btn-outline'}
              style={{ padding: '0.65rem 1.25rem', fontSize: '0.9rem', fontWeight: 700, textTransform: 'uppercase' }}
              data-testid={`filter-${f}`}>
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <button onClick={fetchSubmissions} className="btn-outline" style={{ padding: '0.65rem' }} data-testid="refresh-btn">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)', fontSize: '1.2rem' }}>Loading...</div>
      ) : submissions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: 'var(--surface)', border: '1px solid var(--border-dim)', color: 'var(--text-muted)' }}>
          {search ? 'No submissions match your search.' : 'No submissions yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {submissions.map((sub, i) => {
            const isExpanded = expandedId === sub.id;
            const isNew = !sub.viewed && !sub.printed;
            return (
              <div key={sub.id || i} data-testid={`submission-row-${i}`}
                style={{ background: isNew ? 'rgba(191, 10, 48, 0.06)' : 'var(--surface)', border: isNew ? '2px solid rgba(191, 10, 48, 0.3)' : '1px solid var(--border-dim)', transition: 'all 0.3s' }}>
                {/* Row */}
                <div onClick={() => handleExpand(sub)} data-testid={`submission-toggle-${i}`}
                  style={{ width: '100%', padding: '1.25rem 1.5rem', display: 'flex', gap: '1.5rem', alignItems: 'center', cursor: 'pointer' }}>
                  <div style={{ minWidth: 90 }}>{statusBadge(sub)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'white', marginBottom: '0.2rem' }}>
                      {sub.defendant_name || sub.full_name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      SSN: ***{sub.ssn_last4 || '????'} | {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : 'N/A'}
                      {sub.charge && ` | ${sub.charge}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => handlePrint(sub, 'surety_bond')} className="btn-primary"
                      style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem' }}
                      data-testid={`print-btn-${i}`} disabled={pdfLoading === sub.id}>
                      <Printer size={16} /> {pdfLoading === sub.id ? '...' : 'PRINT'}
                    </button>
                    <button onClick={() => handleDownloadAll(sub)} className="btn-outline"
                      style={{ padding: '0.65rem' }} data-testid={`download-btn-${i}`}>
                      <Download size={16} />
                    </button>
                  </div>
                  <div>{isExpanded ? <ChevronUp size={20} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={20} style={{ color: 'var(--text-muted)' }} />}</div>
                </div>

                {/* Expanded */}
                {isExpanded && expandedData && (
                  <div style={{ padding: '0 1.5rem 1.5rem', borderTop: '1px solid var(--border-dim)' }}>
                    {/* PDF buttons */}
                    <div style={{ display: 'flex', gap: '0.75rem', padding: '1rem 0', borderBottom: '1px solid var(--border-dim)', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      {['personal_reference', 'surety_bond', 'asset_inventory'].map(type => (
                        <button key={type} onClick={() => handlePrint(sub, type)} className="btn-outline"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                          data-testid={`pdf-${type}-btn`}>
                          <Eye size={14} /> {type.replace(/_/g, ' ').toUpperCase()}
                        </button>
                      ))}
                    </div>
                    {/* Data Fields */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem' }}>
                      {Object.entries(expandedData.data || expandedData).map(([key, value]) => {
                        if (!value || key.startsWith('_') || key === 'id' || key === 'pdf_ids' || key === 'data') return null;
                        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        return (
                          <div key={key} style={{ padding: '0.75rem', background: 'rgba(15, 23, 42, 0.5)', border: '1px solid var(--border-dim)' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>{label}</div>
                            <div style={{ fontSize: '0.95rem', color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubmissionsView;
